import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function Hero() {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative w-full h-screen min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image with lazy loading via <img> tag for optimization */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
          alt="Panoramic view of Skardu Valley, Karakoram mountains"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-center hero-bg"
        />
        {/* Dark overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-ocean-900/60 via-indigo-900/50 to-ocean-900/75" />
      </div>

      {/* Scrolling down indicator */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/60 animate-bounce">
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 sm:px-6 max-w-5xl mx-auto w-full">
        {/* Eyebrow label */}
        <p className="section-subtitle text-ocean-300 mb-4 animate-fadeIn">
          Skardu · Gilgit-Baltistan · Pakistan
        </p>

        {/* Lodge name */}
        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-tight mb-4 animate-fadeUp drop-shadow-2xl">
          Mariner Lodges
        </h1>

        {/* Tagline */}
        <p
          className="text-white/85 text-lg sm:text-xl md:text-2xl font-light max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeUp"
          style={{ animationDelay: "0.2s" }}
        >
          A tiny guest house on a panoramic perch above the Indus — where the{" "}
          <span className="italic text-ocean-300 font-normal">mountains meet the sky</span>.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row gap-4 items-center justify-center animate-fadeUp"
          style={{ animationDelay: "0.4s" }}
        >
          <button
            onClick={() => scrollToSection("booking")}
            className="btn-primary text-base px-10 py-4 shadow-2xl shadow-indigo-900/50 w-full sm:w-auto"
            aria-label="Scroll to booking section"
          >
            <span className="flex items-center gap-2 justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Book Now
            </span>
          </button>
          <button
            onClick={() => scrollToSection("contact")}
            className="btn-outline text-base px-10 py-4 w-full sm:w-auto"
            aria-label="Scroll to contact section"
          >
            <span className="flex items-center gap-2 justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Us
            </span>
          </button>
        </div>

        {/* Trust badges */}
        <div
          className="flex flex-wrap items-center justify-center gap-6 mt-14 text-white/60 text-xs tracking-wide uppercase animate-fadeIn"
          style={{ animationDelay: "0.6s" }}
        >
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            5-Star Rated
          </span>
          <span className="w-px h-4 bg-white/20" />
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-ocean-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Panoramic Location
          </span>
          <span className="w-px h-4 bg-white/20" />
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Free Cancellation
          </span>
          <span className="w-px h-4 bg-white/20" />
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-ocean-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
            </svg>
            Solar Powered
          </span>
        </div>
      </div>

      {/* Wave divider at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg
          viewBox="0 0 1440 80"
          className="w-full"
          preserveAspectRatio="none"
          style={{ height: 60 }}
        >
          <path fill="white" d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" />
        </svg>
      </div>
    </section>
  );
}